/**
 * Answering a match offer from an emailed link, with no account.
 *
 * §3.12 asks for "in-app, a web form, fax, or a phone call". This is the web
 * form. The doula it serves may never have signed in — she was found in the
 * provider directory, she was sent a link, and she is deciding whether to take
 * a client. Making her create an account first is how an offer goes unanswered
 * and a mother waits.
 *
 * THE TOKEN IS THE AUTHORITY, so this page sends NO credentials. It uses
 * publicFetchJson rather than authFetch deliberately: an Authorization header
 * here would be meaningless at best, and at worst would answer the offer as
 * whoever last logged in on a shared browser — a phone in a clinic, say.
 *
 * It never claims to know who she is. The token names an offer, not an
 * identity, so the page addresses the decision and not the person; writing
 * "Hello Rosa" from a URL parameter would be stating something unverified.
 *
 * DECLINING ASKS WHY, AND DOES NOT REQUIRE IT. The reason routes the mother to
 * someone else faster, but a doula who is simply unavailable owes no
 * explanation, and blocking her on a required field would cost the answer
 * itself — the thing this page exists to collect.
 */

import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { publicFetchJson } from '@/lib/authFetch';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertCircle, Check, HeartHandshake } from 'lucide-react';

type Outcome = 'accepted' | 'declined' | null;

export default function DoulaRespondPage() {
  const { t } = useTranslation();
  const [search] = useSearchParams();

  const requestId = Number(search.get('request'));
  const panelId = Number(search.get('panel'));
  const token = search.get('token') || '';

  const linkOk = useMemo(
    () => Number.isFinite(requestId) && requestId > 0 && Number.isFinite(panelId) && panelId > 0 && token.length > 0,
    [requestId, panelId, token],
  );

  const [outcome, setOutcome] = useState<Outcome>(null);
  const [showDecline, setShowDecline] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const respond = async (accept: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await publicFetchJson('/api/doula/v1/offers/respond-by-token', {
        method: 'POST',
        jsonBody: {
          request_id: requestId,
          panel_id: panelId,
          token,
          accept,
          reason: accept ? null : reason.trim() || null,
        },
      });
      setOutcome(accept ? 'accepted' : 'declined');
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('doulaRespond.failed', 'We could not record your answer. Please try the link again.'),
      );
    } finally {
      setBusy(false);
    }
  };

  // Sized to its content rather than to the viewport. This page renders on a
  // bare route with no shell, so it would arguably be entitled to own the
  // viewport — but the UX guardrails reserve that for shells and marketing, and
  // say so as scope rather than as an escape hatch. A card at the top of the
  // page reads the same and behaves better on a phone, where vertical centring
  // fights the keyboard.
  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className="mx-auto w-full max-w-md px-4 py-10">
      <div className="rounded-lg border bg-background p-6 shadow-sm">{children}</div>
    </div>
  );

  // A link that is missing its parts is a broken link, not a rejected answer —
  // said before she spends an action on it.
  if (!linkOk) {
    return (
      <Shell>
        <div className="flex items-start gap-2 text-sm">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div>
            <p className="font-medium">{t('doulaRespond.badLinkTitle', 'This link is incomplete.')}</p>
            <p className="mt-1 text-muted-foreground">
              {t('doulaRespond.badLinkBody', 'Please open the link from the message exactly as it was sent, or reply to that message and someone will help.')}
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  if (outcome) {
    return (
      <Shell>
        <div className="flex items-start gap-2 text-sm">
          <Check className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">
              {outcome === 'accepted'
                ? t('doulaRespond.acceptedTitle', 'Thank you — you have taken this client.')
                : t('doulaRespond.declinedTitle', 'Thank you for letting us know.')}
            </p>
            <p className="mt-1 text-muted-foreground">
              {outcome === 'accepted'
                ? t('doulaRespond.acceptedBody', 'We will be in touch with what happens next. You do not need to do anything else here.')
                : t('doulaRespond.declinedBody', 'We will offer this client to someone else. Nothing further is needed from you.')}
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-4 flex items-center gap-2">
        <HeartHandshake className="h-5 w-5" />
        <h1 className="text-base font-semibold">{t('doulaRespond.title', 'A client is being referred to you')}</h1>
      </div>

      <p className="text-sm text-muted-foreground">
        {t('doulaRespond.body', 'Someone looking for a doula has been matched with you. Would you like to take them on?')}
      </p>

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!showDecline ? (
        <div className="mt-5 flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => void respond(true)}>
            {t('doulaRespond.accept', 'Yes, I can take them')}
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => setShowDecline(true)}>
            {t('doulaRespond.decline', 'Not right now')}
          </Button>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          <div>
            <Label htmlFor="decline-reason">
              {t('doulaRespond.reasonLabel', 'Anything you would like us to know? (optional)')}
            </Label>
            <Textarea
              id="decline-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1"
              placeholder={t('doulaRespond.reasonPlaceholder', 'For example: my caseload is full until March.')}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => void respond(false)}>
              {t('doulaRespond.sendDecline', 'Send')}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setShowDecline(false)}>
              {t('common.back', 'Back')}
            </Button>
          </div>
        </div>
      )}
    </Shell>
  );
}
